// client/src/components/plataforma/users/sections/ProfileSection.js
import React from 'react';
import { User, Mail, Phone, Calendar, Briefcase, Hash } from 'lucide-react';

const ProfileSection = ({ user, contextData, isCreating, onChange }) => {
    if (!user) return null;

    const handleChange = (field, value) => {
        if (onChange) {
            onChange(field, value);
        }
    };

    return (
        <div className="form-content">
            <div className="form-header">
                <h3 className="form-title">
                    <User size={20} className="inline mr-2" />
                    Dados Pessoais
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                    {isCreating 
                        ? 'Preencha os dados básicos do usuário. Telefone e Nome são obrigatórios.'
                        : 'Edite os dados pessoais do usuário'}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nome - Obrigatório */}
                <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1 required">
                        Nome Completo
                    </label>
                    <div className="relative">
                        <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={user.name || ''}
                            onChange={(e) => handleChange('name', e.target.value)}
                            className="form-input pl-10"
                            placeholder="Nome completo"
                            required
                        />
                    </div>
                </div>

                {/* Telefone - Obrigatório */}
                <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1 required">
                        Telefone
                    </label>
                    <div className="relative">
                        <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="tel"
                            value={user.telefone || ''}
                            onChange={(e) => handleChange('telefone', e.target.value)}
                            className="form-input pl-10"
                            placeholder="(00) 00000-0000"
                            required
                        />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Apenas números, com DDD
                    </p>
                </div>

                {/* Email - Opcional */}
                <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        E-mail
                    </label>
                    <div className="relative">
                        <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="email"
                            value={user.email || ''}
                            onChange={(e) => handleChange('email', e.target.value)}
                            className="form-input pl-10"
                            placeholder="email@exemplo.com"
                        />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Opcional. Se não informado, será gerado automaticamente.
                    </p>
                </div>

                {/* CPF - Opcional */}
                <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        CPF
                    </label>
                    <div className="relative">
                        <Hash size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={user.cpf || ''}
                            onChange={(e) => handleChange('cpf', e.target.value)}
                            className="form-input pl-10"
                            placeholder="000.000.000-00"
                        />
                    </div>
                </div>

                {/* Data de Nascimento - Opcional */}
                <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Nascimento
                    </label>
                    <div className="relative">
                        <Calendar size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="date"
                            value={user.dataNascimento || ''}
                            onChange={(e) => handleChange('dataNascimento', e.target.value)}
                            className="form-input pl-10"
                        />
                    </div>
                </div>

                {/* Role */}
                {contextData?.isOwnProfile ? (
                    // ✅ CORREÇÃO: no autoatendimento ("Minha Conta"), o papel nunca
                    // aparecia — este campo comparava `user.role` (objeto populado
                    // vindo de useMe(), {_id, name, permissions...}) contra opções
                    // fixas de string ('admin'/'profissional'/...) que nunca batiam
                    // com o Role real da conta, então o <select> sempre ficava vazio.
                    // Além disso, deixava o próprio usuário trocar seu papel — nunca
                    // deveria ser editável no autoatendimento. Agora exibe o nome do
                    // papel real, somente leitura.
                    <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Papel (Role)
                        </label>
                        <div className="relative">
                            <Briefcase size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={user.role?.name || 'Sem papel definido'}
                                disabled
                                readOnly
                                className="form-input pl-10"
                                style={{ background: 'var(--color-bg-alt)', color: 'var(--color-text-secondary)', cursor: 'not-allowed' }}
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Definido pelo administrador da conta — fale com quem administra sua conta pra alterar.
                        </p>
                    </div>
                ) : (
                    <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1 required">
                            Papel (Role)
                        </label>
                        <div className="relative">
                            <Briefcase size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <select
                                value={user.role || ''}
                                onChange={(e) => handleChange('role', e.target.value)}
                                className="form-input pl-10"
                                required
                            >
                                <option value="">Selecione um papel...</option>
                                <option value="admin">Administrador</option>
                                <option value="profissional">Profissional</option>
                                <option value="atendente">Atendente</option>
                                <option value="financeiro">Financeiro</option>
                            </select>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                            Define as permissões básicas do usuário no sistema
                        </p>
                    </div>
                )}

                {/* ✅ CHECKBOX REMOVIDO: A FLAG AGORA ESTÁ NA ABA DE PERMISSÕES! */}
            </div>
        </div>
    );
};

export default ProfileSection;